import BcSkiIcon from "@/assets/sport-icons/bc-ski.svg?react";
import BikeIcon from "@/assets/sport-icons/bike.svg?react";
import CanoeIcon from "@/assets/sport-icons/canoe.svg?react";
import ClimbIcon from "@/assets/sport-icons/climb.svg?react";
import EBikeIcon from "@/assets/sport-icons/e-bike.svg?react";
import EMtbIcon from "@/assets/sport-icons/e-mtb.svg?react";
import GravelIcon from "@/assets/sport-icons/gravel.svg?react";
import HikeIcon from "@/assets/sport-icons/hike.svg?react";
import IndoorBikeIcon from "@/assets/sport-icons/indoor-bike.svg?react";
import IndoorRowIcon from "@/assets/sport-icons/indoor-row.svg?react";
import MtbIcon from "@/assets/sport-icons/mtb.svg?react";
import NordicIcon from "@/assets/sport-icons/nordic.svg?react";
import RollerSkiIcon from "@/assets/sport-icons/roller-ski.svg?react";
import RowIcon from "@/assets/sport-icons/row.svg?react";
import RunIcon from "@/assets/sport-icons/run.svg?react";
import SkiIcon from "@/assets/sport-icons/ski.svg?react";
import SnowboardIcon from "@/assets/sport-icons/snowboard.svg?react";
import SnowshoeIcon from "@/assets/sport-icons/snowshoe.svg?react";
import SwimIcon from "@/assets/sport-icons/swim.svg?react";
import TrailIcon from "@/assets/sport-icons/trail.svg?react";
import WalkIcon from "@/assets/sport-icons/walk.svg?react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Activity as ActivityIcon, Dumbbell } from "lucide-react";
import {
  SPORT_TYPES,
  type SportIconComponent,
  type SportType,
} from "./data.types";

export const SPORT_LABELS: Record<SportType, MessageDescriptor> = {
  AlpineSki: msg`Alpine ski`,
  BackcountrySki: msg`Backcountry ski`,
  Badminton: msg`Badminton`,
  Canoeing: msg`Canoeing`,
  Crossfit: msg`CrossFit`,
  EBikeRide: msg`E-bike ride`,
  Elliptical: msg`Elliptical`,
  EMountainBikeRide: msg`E-MTB ride`,
  Golf: msg`Golf`,
  GravelRide: msg`Gravel ride`,
  Handcycle: msg`Handcycle`,
  HighIntensityIntervalTraining: msg`HIIT`,
  Hike: msg`Hike`,
  IceSkate: msg`Ice skate`,
  InlineSkate: msg`Inline skate`,
  Kayaking: msg`Kayaking`,
  Kitesurf: msg`Kitesurf`,
  MountainBikeRide: msg`Mountain bike ride`,
  NordicSki: msg`Nordic ski`,
  Pickleball: msg`Pickleball`,
  Pilates: msg`Pilates`,
  Racquetball: msg`Racquetball`,
  Ride: msg`Ride`,
  RockClimbing: msg`Rock climbing`,
  RollerSki: msg`Roller ski`,
  Rowing: msg`Rowing`,
  Run: msg`Run`,
  Sail: msg`Sailing`,
  Skateboard: msg`Skateboard`,
  Snowboard: msg`Snowboard`,
  Snowshoe: msg`Snowshoe`,
  Soccer: msg`Soccer`,
  Squash: msg`Squash`,
  StairStepper: msg`Stair stepper`,
  StandUpPaddling: msg`Stand up paddling`,
  Surfing: msg`Surfing`,
  Swim: msg`Swim`,
  TableTennis: msg`Table tennis`,
  Tennis: msg`Tennis`,
  TrailRun: msg`Trail run`,
  Velomobile: msg`Velomobile`,
  VirtualRide: msg`Virtual ride`,
  VirtualRow: msg`Virtual rowing`,
  VirtualRun: msg`Virtual run`,
  Walk: msg`Walk`,
  WeightTraining: msg`Weight training`,
  Wheelchair: msg`Wheelchair`,
  Windsurf: msg`Windsurf`,
  Workout: msg`Workout`,
  Yoga: msg`Yoga`,
};

// Helper for UI: i18n._(SPORT_LABELS[s]) is the translated string
export function sportLabel(s: SportType): MessageDescriptor {
  return SPORT_LABELS[s] ?? msg`${s}`;
}

const onFoot: SportType[] = ["Run", "TrailRun", "Hike", "Walk"];
const cycling: SportType[] = [
  "Ride",
  "GravelRide",
  "MountainBikeRide",
  "EBikeRide",
  "EMountainBikeRide",
  "VirtualRide",
  "Handcycle",
  "Velomobile",
];
const water: SportType[] = [
  "Swim",
  "Rowing",
  "Canoeing",
  "Kayaking",
  "Sail",
  "Surfing",
  "StandUpPaddling",
  "Kitesurf",
  "Windsurf",
];
const winter: SportType[] = [
  "AlpineSki",
  "BackcountrySki",
  "NordicSki",
  "Snowboard",
  "Snowshoe",
  "RollerSki",
];
const racketSports: SportType[] = [
  "Tennis",
  "TableTennis",
  "Badminton",
  "Squash",
  "Racquetball",
  "Pickleball",
];
const training: SportType[] = [
  "Workout",
  "WeightTraining",
  "Crossfit",
  "HighIntensityIntervalTraining",
  "Pilates",
  "Yoga",
  "Elliptical",
  "StairStepper",
];
const other: SportType[] = [
  "RockClimbing",
  "IceSkate",
  "InlineSkate",
  "Skateboard",
  "Soccer",
  "Wheelchair",
  "VirtualRun",
  "VirtualRow",
  "Golf",
];

// Build a set of everything already categorized (excluding highlights, which we allow to duplicate)
const categorized = new Set<SportType>([
  ...onFoot,
  ...cycling,
  ...water,
  ...winter,
  ...racketSports,
  ...training,
  ...other,
]);

// Any stragglers from SPORT_TYPES go into "Other"
const missing = SPORT_TYPES.filter((s) => !categorized.has(s));
const otherFinal = [...other, ...missing];

export type SportSection = { title: MessageDescriptor; sports: SportType[] };

export const defaultSections: SportSection[] = [
  { title: msg`On foot`, sports: onFoot },
  { title: msg`Cycling`, sports: cycling },
  { title: msg`Water`, sports: water },
  { title: msg`Winter`, sports: winter },
  { title: msg`Racket sports`, sports: racketSports },
  { title: msg`Training`, sports: training },
  { title: msg`Other`, sports: otherFinal },
];

/**
 * Color strategy:
 * - On-foot: greens
 * - Cycling: blues
 * - Water: teals
 * - Winter: icy blues / purples
 * - Racket: oranges/yellows
 * - Strength/training: reds/magentas
 *
 * Use CSS variables if you have them, or keep these hex values.
 */
export const SPORT_COLORS: Record<SportType, string> = {
  // On foot (greens)
  Run: "#22c55e",
  TrailRun: "#16a34a",
  Walk: "#84cc16",
  Hike: "#65a30d",

  // Cycling (blues)
  Ride: "#3b82f6",
  MountainBikeRide: "#2563eb",
  GravelRide: "#60a5fa",
  EBikeRide: "#1d4ed8",
  EMountainBikeRide: "#1e40af",
  VirtualRide: "#38bdf8",
  Handcycle: "#0ea5e9",
  Velomobile: "#0284c7",

  // Water (teals)
  Swim: "#14b8a6",
  Rowing: "#06b6d4",
  Canoeing: "#0891b2",
  Kayaking: "#0ea5e9",
  Surfing: "#2dd4bf",
  StandUpPaddling: "#10b981",
  Sail: "#0d9488",
  Kitesurf: "#22d3ee",
  Windsurf: "#06b6d4",

  // Winter (icy)
  AlpineSki: "#a78bfa",
  BackcountrySki: "#7c3aed",
  NordicSki: "#c4b5fd",
  Snowboard: "#8b5cf6",
  Snowshoe: "#ddd6fe",
  RollerSki: "#6d28d9",
  IceSkate: "#93c5fd",

  // Racket / court (warm)
  Tennis: "#f59e0b",
  TableTennis: "#fbbf24",
  Badminton: "#fb923c",
  Squash: "#f97316",
  Racquetball: "#ea580c",
  Pickleball: "#fcd34d",

  // Training / gym (reds/pinks)
  WeightTraining: "#ef4444",
  Workout: "#f43f5e",
  Crossfit: "#e11d48",
  HighIntensityIntervalTraining: "#be123c",
  Pilates: "#ec4899",
  Yoga: "#db2777",
  Elliptical: "#fb7185",
  StairStepper: "#fda4af",

  // Other
  RockClimbing: "#a16207",
  Golf: "#a3e635",
  Skateboard: "#f472b6",
  InlineSkate: "#fb7185",
  Soccer: "#fde047",
  Wheelchair: "#64748b",
  VirtualRun: "#34d399",
  VirtualRow: "#22d3ee",
};

export function sportIcon(sportOrType?: string | null): SportIconComponent {
  const s = (sportOrType ?? "").toLowerCase();

  if (s.includes("emountainbike")) return EMtbIcon;
  if (s.includes("gravel")) return GravelIcon;
  if (s.includes("mountainbike")) return MtbIcon;
  if (s.includes("virtualride")) return IndoorBikeIcon;
  if (s.includes("ebike")) return EBikeIcon;
  if (s.includes("ride")) return BikeIcon;
  if (s.includes("walk")) return WalkIcon;
  if (s.includes("trail")) return TrailIcon;
  if (s.includes("run")) return RunIcon;
  if (s.includes("hike")) return HikeIcon;
  if (s.includes("climb")) return ClimbIcon;
  if (s.includes("nordic")) return NordicIcon;
  if (s.includes("backcountryski")) return BcSkiIcon;
  if (s.includes("snowboard")) return SnowboardIcon;
  if (s.includes("snowshoe")) return SnowshoeIcon;
  if (s.includes("rollerski")) return RollerSkiIcon;
  if (s.includes("ski")) return SkiIcon;
  if (s.includes("swim")) return SwimIcon;
  if (s.includes("virtualrow")) return IndoorRowIcon;
  if (s.includes("row")) return RowIcon;
  if (s.includes("canoe")) return CanoeIcon;

  if (
    s.includes("workout") ||
    s.includes("training") ||
    s.includes("crossfit") ||
    s.includes("weight")
  )
    return Dumbbell;

  return ActivityIcon;
}
